import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import ComparisonSelector from './components/ComparisonSelector';
import ComparisonResults from './components/ComparisonResults';
import VehicleList from './components/VehicleList';
import CostCalculator from './components/CostCalculator';
import Filters from './components/Filters';
import ManufacturerGrid from './components/ManufacturerGrid';
import Modal from './components/Modal';
import FormModal from './components/FormModal';
import Toast from './components/Toast';
import ServiceCards from './components/ServiceCards';
import TrustBadge from './components/TrustBadge';
import LegalFooter from './components/LegalFooter';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import { AppProvider, AppContext } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getPublicBrands, getVehicles } from './api/client';
import { CHINESE_MFGS, MANUFACTURERS, ORIGIN_TITLES } from './utils/catalog';

function AppShell() {
  const { openInquiryForm, requestTestDrive, requestService, selectedVehicle, showToast } =
    React.useContext(AppContext);
  const { user } = useAuth();
  const [allVehicles, setAllVehicles] = useState([]);
  const [dynamicBrands, setDynamicBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [originFilter, setOriginFilter] = useState('all');
  const [secondaryFilter, setSecondaryFilter] = useState('all');
  const [mfgFilter, setMfgFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const costRef = useRef(null);

  // Reset to page 1 whenever filters, search, or manufacturer change
  useEffect(() => {
    setCurrentPage(1);
  }, [originFilter, secondaryFilter, mfgFilter, searchTerm]);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [vData, bData] = await Promise.all([
        getVehicles({ limit: 200 }),
        getPublicBrands().catch(() => []),
      ]);
      setAllVehicles(vData);
      setDynamicBrands(bData || []);
    } catch (err) {
      console.error(err);
      setError('Could not load vehicles. Check that the API is running on port 9019.');
      setAllVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const manufacturers = useMemo(() => {
    const dynMap = new Map();
    for (const b of dynamicBrands) {
      dynMap.set(b.slug, b);
    }

    const list = MANUFACTURERS.map((m) => {
      const dyn = dynMap.get(m.id);
      return {
        ...m,
        label: dyn?.name || m.label,
        logo_url: dyn?.logo_url || null,
        logo: dyn?.logo || null,
      };
    });

    for (const b of dynamicBrands) {
      if (!list.some((m) => m.id === b.slug)) {
        list.push({
          id: b.slug,
          label: b.name,
          hint: b.country || (b.origin === 'chinese' ? 'CN' : 'PK'),
          logo_url: b.logo_url || null,
          logo: b.logo || null,
        });
      }
    }
    return list;
  }, [dynamicBrands]);

  const vehicles = useMemo(() => {
    let rows = [...allVehicles];
    if (mfgFilter) {
      rows = rows.filter((v) => (v.mfg || '').toLowerCase() === mfgFilter.toLowerCase());
    } else if (originFilter === 'assembled') {
      rows = rows.filter((v) => v.origin === 'assembled' || v.origin === 'PK');
    } else if (originFilter === 'imported') {
      rows = rows.filter((v) => v.origin === 'imported');
    } else if (originFilter === 'chinese') {
      const chineseSlugs = dynamicBrands
        .filter((b) => (b.origin || '').toLowerCase() === 'chinese')
        .map((b) => b.slug);
      const allChinese = new Set([...CHINESE_MFGS, ...chineseSlugs]);
      rows = rows.filter((v) => allChinese.has((v.mfg || '').toLowerCase()) || v.origin === 'chinese');
    }

    if (secondaryFilter && secondaryFilter !== 'all') {
      if (secondaryFilter === 'new' || secondaryFilter === 'used') {
        rows = rows.filter((v) => v.condition === secondaryFilter);
      } else if (['petrol', 'diesel', 'ev'].includes(secondaryFilter)) {
        rows = rows.filter((v) => v.powertrain === secondaryFilter);
      }
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      rows = rows.filter(
        (v) =>
          v.name?.toLowerCase().includes(q) ||
          v.mfg?.toLowerCase().includes(q) ||
          v.category?.toLowerCase().includes(q) ||
          v.variant?.toLowerCase().includes(q),
      );
    }
    return rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allVehicles, dynamicBrands, originFilter, secondaryFilter, mfgFilter, searchTerm]);

  const categoryTitle = mfgFilter
    ? `${(manufacturers.find((m) => m.id === mfgFilter)?.label || mfgFilter).toUpperCase()} · ${vehicles.length} models`
    : ORIGIN_TITLES[originFilter] || 'All Vehicles';


  const handleSelectMfg = (mfgId) => {
    if (mfgFilter === mfgId) {
      setMfgFilter('');
    } else {
      setMfgFilter(mfgId);
      setOriginFilter('all');
    }
    setCurrentPage(1);
  };

  const handleOriginFilter = (id) => {
    setOriginFilter(id);
    setMfgFilter('');
    setCurrentPage(1);
  };

  const scrollToCost = () => {
    costRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="app-container">
      <header className="brand-header">
        <div className="brand">
          <i className="fas fa-car" />
          <h1>
            AutoCompare <small>PK</small>
          </h1>
        </div>
        <div className="header-actions">
          <Link to={user ? '/dashboard/inquiries' : '/login?next=/dashboard/inquiries'} className="dashboard-link">
            <i className="fas fa-gauge-high" /> Dashboard
          </Link>
          <TrustBadge />
        </div>
      </header>

      <div className="tagline">
        <i className="fas fa-check-circle" />
        <span>Assembled · Imported · Chinese Brands</span>
        <span>
          <i className="fas fa-flag" /> all makes
        </span>
      </div>

      <ComparisonSelector vehicles={allVehicles} />
      <ComparisonResults />

      <Filters
        originFilter={originFilter}
        setOriginFilter={handleOriginFilter}
        secondaryFilter={secondaryFilter}
        setSecondaryFilter={setSecondaryFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <ManufacturerGrid activeMfg={mfgFilter} onSelectMfg={handleSelectMfg} manufacturers={manufacturers} />

      <VehicleList
        vehicles={vehicles}
        loading={loading}
        error={error}
        categoryTitle={categoryTitle}
        onRetry={loadCatalog}
        currentPage={currentPage}
        pageSize={10}
        onPageChange={setCurrentPage}
      />

      <div ref={costRef}>
        <CostCalculator />
      </div>

      <ServiceCards />

      <div className="trust-slim">
        <button
          type="button"
          className="trust-slim-item"
          onClick={() => requestService({ applicationType: 'verified' })}
        >
          <i className="fas fa-check-double" /> Verified listings
        </button>
        <button
          type="button"
          className="trust-slim-item"
          onClick={() => requestService({ applicationType: 'history' })}
        >
          <i className="fas fa-file-signature" /> Vehicle history
        </button>
        <button
          type="button"
          className="trust-slim-item"
          onClick={() => requestService({ applicationType: 'protection' })}
        >
          <i className="fas fa-hand-holding-heart" /> Buyer protection
        </button>
      </div>

      <LegalFooter />

      <div className="universal-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            if (!selectedVehicle) {
              showToast('Please select a vehicle from the list first');
              return;
            }
            openInquiryForm({
              title: 'Request Info',
              vehicle: selectedVehicle,
              source: 'universal_bar',
            });
          }}
        >
          <i className="fas fa-envelope" /> Request Info
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() =>
            requestTestDrive({
              title: 'Book a Test Drive',
              applicationType: 'testdrive',
              vehicle: selectedVehicle,
              source: 'universal_bar',
            })
          }
        >
          <i className="fas fa-key" /> Test Drive
        </button>
        <button type="button" className="btn-cost" onClick={scrollToCost}>
          <i className="fas fa-calculator" /> Running Costs
        </button>
      </div>

      <Modal />
      <FormModal />
      <Toast />
    </div>
  );
}

function AppRoutes() {
  return (
    <AppProvider>
      <Routes>
        <Route path="/" element={<AppShell />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/dashboard/*" element={<DashboardPage />} />
      </Routes>
    </AppProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
